import React, { useContext, useState, useEffect, useReducer, useRef } from "react";
import "emoji-mart/css/emoji-mart.css";

import { isSameDay, parseISO, format } from "date-fns";
import { Picker } from "emoji-mart";
import openSocket from "../../services/socket-io";
import clsx from "clsx";

import { green } from "@material-ui/core/colors";
import {
  Button,
  ClickAwayListener,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  makeStyles,
  Popover,
  Typography,
} from "@material-ui/core";
import {
  AccessTime,
  Add,
  Block,
  Done,
  DoneAll,
  ExpandMore,
  FormatListBulleted,
  GetApp,
  InsertDriveFile,
  InsertEmoticon,
  FileCopy,
} from "@material-ui/icons";
import { toast } from "react-toastify";

import MarkdownWrapper from "../MarkdownWrapper";
import VcardPreview from "../VcardPreview";
import LocationPreview from "../LocationPreview";
import ModalImageCors from "../ModalImageCors";
import MessageOptionsMenu from "../MessageOptionsMenu";
import chatBackgroundMask from "../../assets/background.svg";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { dispatchTicketRead } from "../../utils/ticketEvents";
import Audio from "../Audio";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
  getStoredPrivacyCensorSettings,
  privacyCensorChangedEvent,
} from "../../utils/privacyCensor";

const useStyles = makeStyles((theme) => ({
  messagesListWrapper: {
    overflow: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    backgroundColor: "#efeae2",
    "&::before": {
      content: '""',
      position: "absolute",
      inset: 0,
      pointerEvents: "none",
      backgroundColor: "rgba(0, 0, 0, 0.08)",
      maskImage: `url(${chatBackgroundMask})`,
      maskRepeat: "repeat",
      maskMode: "alpha",
      maskSize: "540px 960px",
      WebkitMaskImage: `url(${chatBackgroundMask})`,
      WebkitMaskRepeat: "repeat",
      WebkitMaskSize: "540px 960px",
    },
  },

  messagesList: {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    position: "relative",
    padding: "20px 60px 20px 60px",
    overflowY: "scroll",
    [theme.breakpoints.down("sm")]: {
      padding: "20px 20px 20px 60px",
      marginBottom: 64,
    },
    ...theme.scrollbarStyles,
  },
  messagesListWithAiTools: {
    [theme.breakpoints.down("sm")]: {
      marginBottom: 106,
    },
  },

  circleLoading: {
    color: green[500],
    position: "absolute",
    opacity: "70%",
    top: 0,
    left: "50%",
    marginTop: 12,
  },

  messageLeft: {
    marginRight: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 390,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },
    "&:hover $messageQuickActions": {
      opacity: 1,
      pointerEvents: "auto",
    },
    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      right: -100,
      width: 100,
      height: "100%",
    },

    whiteSpace: "pre-wrap",
    backgroundColor: "#ffffff",
    color: "#303030",
    alignSelf: "flex-start",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: "0 1px 1px #b3b3b3",
  },

  quotedContainerLeft: {
    margin: "-3px -80px 6px -6px",
    overflow: "hidden",
    backgroundColor: "#f0f0f0",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsg: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  quotedSideColorLeft: {
    flex: "none",
    width: "4px",
    backgroundColor: "#6bcbef",
  },

  messageRight: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 390,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },
    "&:hover $messageQuickActions": {
      opacity: 1,
      pointerEvents: "auto",
    },
    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      left: -100,
      width: 100,
      height: "100%",
    },

    whiteSpace: "pre-wrap",
    backgroundColor: "#dcf8c6",
    color: "#303030",
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: "0 1px 1px #b3b3b3",
  },
  messageInternalNote: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 120,
    maxWidth: 390,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },
    "&:hover $messageQuickActions": {
      opacity: 1,
      pointerEvents: "auto",
    },
    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      left: -100,
      width: 100,
      height: "100%",
    },

    whiteSpace: "pre-wrap",
    backgroundColor: "#fff3b0",
    color: "#3f3400",
    alignSelf: "flex-end",
    border: "1px solid #f0d467",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: "0 1px 1px rgba(125, 98, 0, 0.35)",
  },
  internalNoteLabel: {
    display: "block",
    color: "#8a6d00",
    fontSize: 11,
    fontWeight: 700,
    margin: "0 80px 2px 6px",
  },

  messageSourceLabel: {
    display: "inline-flex",
    alignItems: "center",
    width: "fit-content",
    gap: 4,
    fontSize: 10,
    fontWeight: 700,
    lineHeight: "14px",
    borderRadius: 999,
    padding: "1px 7px",
    margin: "0px 8px 2px 0px",
    letterSpacing: 0,
    textTransform: "uppercase",
  },

  aiSourceLabel: {
    color: "#064e3b",
    backgroundColor: "#ccfbf1",
    border: "1px solid #5eead4",
  },

  agentSourceLabel: {
    color: "#084298",
    backgroundColor: "#cfe2ff",
    border: "1px solid #b6d4fe",
  },

  funnelSourceLabel: {
    color: "#0f5132",
    backgroundColor: "#d1e7dd",
    border: "1px solid #badbcc",
  },

  campaignSourceLabel: {
    color: "#7c2d12",
    backgroundColor: "#ffedd5",
    border: "1px solid #fdba74",
  },

  agendaSourceLabel: {
    color: "#14532d",
    backgroundColor: "#dcfce7",
    border: "1px solid #86efac",
  },

  webhookSourceLabel: {
    color: "#3730a3",
    backgroundColor: "#e0e7ff",
    border: "1px solid #a5b4fc",
  },

  quickReplySourceLabel: {
    color: "#581c87",
    backgroundColor: "#f3e8ff",
    border: "1px solid #d8b4fe",
  },

  quotedContainerRight: {
    margin: "-3px -80px 6px -6px",
    overflowY: "hidden",
    backgroundColor: "#cfe9ba",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsgRight: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    whiteSpace: "pre-wrap",
  },

  quotedSideColorRight: {
    flex: "none",
    width: "4px",
    backgroundColor: "#35cd96",
  },

  messageActionsButton: {
    display: "none",
    position: "relative",
    color: "#999",
    zIndex: 1,
    backgroundColor: "inherit",
    opacity: "90%",
    "&:hover, &.Mui-focusVisible": { backgroundColor: "inherit" },
  },

  messageContactName: {
    display: "flex",
    color: "#6bcbef",
    fontWeight: 500,
  },

  textContentItem: {
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
    [theme.breakpoints.down("sm")]: {
      padding: "10px 5px",
    },
    "& p": {
      margin: 0,
      marginBottom: theme.spacing(1),
    },
    "& p:last-child": {
      marginBottom: 0,
    },
    "& h1, & h2, & h3, & h4, & h5, & h6": {
      fontSize: "inherit",
      lineHeight: 1.35,
      fontWeight: 700,
      margin: theme.spacing(1, 0, 0.5),
    },
    "& h1:first-child, & h2:first-child, & h3:first-child, & h4:first-child, & h5:first-child, & h6:first-child": {
      marginTop: 0,
    },
    "& ul, & ol": {
      margin: theme.spacing(0.5, 0, 1),
      paddingLeft: theme.spacing(2.5),
    },
    "& li": {
      marginBottom: theme.spacing(0.25),
    },
    "& blockquote": {
      margin: theme.spacing(0.5, 0),
      paddingLeft: theme.spacing(1),
      borderLeft: "3px solid rgba(0,0,0,.18)",
    },
  },
  censoredText: {
    padding: "1px 8px",
    filter: "blur(3px)",
    userSelect: "none",
  },
  censoredMedia: {
    borderRadius: 10,
    filter: "blur(14px)",
    userSelect: "none",
    overflow: "hidden",
  },

  textContentItemDeleted: {
    fontStyle: "italic",
    color: "rgba(0, 0, 0, 0.36)",
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
    [theme.breakpoints.down("sm")]: {
      padding: "10px 5px",
    },
  },

  messageMedia: {
    objectFit: "cover",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    width: "stretch",
    maxWidth: "100%",
  },

  timestamp: {
    fontSize: 11,
    position: "absolute",
    bottom: 0,
    right: 5,
    color: "#999",
  },

  messageReactions: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 4,
    margin: "0 58px 5px 6px",
  },

  messageReactionsRight: {
    justifyContent: "flex-end",
  },

  messageReactionChip: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 24,
    minHeight: 22,
    padding: "1px 7px",
    borderRadius: 999,
    backgroundColor: "#ffffff",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.16)",
    fontSize: 15,
    lineHeight: "18px",
    whiteSpace: "nowrap",
  },

  messageQuickActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "absolute",
    top: "50%",
    right: -70,
    transform: "translateY(-50%)",
    zIndex: 5,
    gap: 4,
    opacity: 0,
    pointerEvents: "none",
    transition: "opacity 120ms ease",
  },

  messageQuickActionsRight: {
    right: "auto",
    left: -70,
  },

  messageQuickActionsOpen: {
    opacity: 1,
    pointerEvents: "auto",
  },

  quickActionButton: {
    width: 30,
    height: 30,
    minWidth: 30,
    padding: 0,
    color: "#667781",
    backgroundColor: "#ffffff",
    border: "none",
    boxShadow: "none",
    transition: "opacity 120ms ease, color 120ms ease, background-color 120ms ease",
    "&:hover": {
      color: "#111b21",
      backgroundColor: "#ffffff",
    },
    "&.Mui-focusVisible": {
      backgroundColor: "#ffffff",
    },
  },

  reactionEntryButtonOpen: {
    color: "#111b21",
    backgroundColor: "#ffffff",
  },

  reactionPickerPaper: {
    borderRadius: 999,
    backgroundColor: "#ffffff",
    border: "1px solid rgba(17, 27, 33, 0.08)",
    boxShadow: "0 2px 8px rgba(17, 27, 33, 0.14)",
    overflow: "visible",
  },

  reactionPickerPaperFull: {
    borderRadius: 8,
    overflow: "hidden",
  },

  reactionPickerContent: {
    display: "flex",
    alignItems: "center",
    gap: 2,
    padding: "5px 8px",
  },

  reactionPickerButton: {
    width: 32,
    height: 32,
    minWidth: 32,
    padding: 0,
    color: "#111b21",
    fontSize: 20,
    lineHeight: "32px",
    transition: "transform 120ms ease",
    "&:hover": {
      backgroundColor: "#f0f2f5",
      transform: "translateY(-2px) scale(1.14)",
    },
  },

  reactionPickerPlus: {
    color: "#54656f",
    fontSize: 19,
  },

  reactionEmojiPickerContent: {
    "& .emoji-mart": {
      border: "none",
      boxShadow: "none",
    },
  },

  dailyTimestamp: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    width: "110px",
    backgroundColor: "#e1f3fb",
    margin: "10px",
    borderRadius: "10px",
    boxShadow: "0 1px 1px #b3b3b3",
  },

  dailyTimestampText: {
    color: "#808888",
    padding: 8,
    alignSelf: "center",
    marginLeft: "0px",
  },

  ackIcons: {
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  deletedIcon: {
    fontSize: 18,
    verticalAlign: "middle",
    marginRight: 4,
  },

  ackDoneAllIcon: {
    color: green[500],
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  documentShell: {
    minWidth: 260,
    maxWidth: 360,
    borderRadius: 8,
    overflow: "hidden",
    marginBottom: 2,
    [theme.breakpoints.down("sm")]: {
      minWidth: 220,
      maxWidth: "calc(100vw - 104px)",
    },
  },
  documentPreview: {
    height: 127,
    width: "calc(100% + 20px)",
    border: 0,
    display: "block",
    backgroundColor: "#ffffff",
    borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
    pointerEvents: "none",
    marginLeft: -2,
    marginTop: -2,
    marginRight: -18,
  },
  documentCard: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 12px",
    color: "#111b21",
    textDecoration: "none",
    overflow: "hidden",
    backgroundColor: "rgba(255, 255, 255, 0.24)",
    "&:hover": {
      textDecoration: "none",
      backgroundColor: "rgba(255, 255, 255, 0.36)",
    },
  },
  documentIconWrap: {
    width: 34,
    height: 42,
    flex: "0 0 34px",
    borderRadius: 6,
    backgroundColor: "#f15c5c",
    color: "#fff",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "inset 0 -1px 0 rgba(0, 0, 0, 0.14)",
  },
  documentIcon: {
    fontSize: 22,
    lineHeight: 1,
  },
  documentExtension: {
    maxWidth: 30,
    marginTop: -2,
    fontSize: 8,
    lineHeight: "10px",
    fontWeight: 700,
    textTransform: "uppercase",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  documentInfo: {
    minWidth: 0,
    flex: "1 1 auto",
  },
  documentName: {
    display: "block",
    fontSize: 14,
    lineHeight: "18px",
    fontWeight: 500,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  documentMeta: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginTop: 3,
    color: "#667781",
    fontSize: 11,
    lineHeight: "14px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  documentDownloadIcon: {
    flex: "0 0 auto",
    color: "#667781",
    fontSize: 22,
  },
  pollBubble: {
    minWidth: 250,
    maxWidth: 330,
    padding: "8px 8px 4px",
  },
  pollTitle: {
    fontSize: 15,
    fontWeight: 600,
    lineHeight: "20px",
    marginBottom: 8,
    color: "#111b21",
  },
  pollHint: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    color: "#667781",
    marginBottom: 8,
  },
  pollOption: {
    display: "grid",
    gridTemplateColumns: "24px 1fr auto",
    columnGap: 8,
    alignItems: "center",
    padding: "6px 0 8px",
  },
  pollCheck: {
    width: 18,
    height: 18,
    borderRadius: "50%",
    border: "2px solid #8696a0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#00a884",
  },
  pollCheckSelected: {
    borderColor: "#00a884",
    backgroundColor: "rgba(0, 168, 132, 0.12)",
  },
  pollOptionName: {
    fontSize: 14,
    lineHeight: "18px",
    color: "#111b21",
    overflowWrap: "anywhere",
  },
  pollVotes: {
    fontSize: 12,
    color: "#667781",
    minWidth: 18,
    textAlign: "right",
  },
  pollProgressTrack: {
    gridColumn: "2 / 4",
    height: 4,
    borderRadius: 999,
    backgroundColor: "rgba(134, 150, 160, 0.25)",
    overflow: "hidden",
    marginTop: 4,
  },
  pollProgressBar: {
    height: "100%",
    borderRadius: 999,
    backgroundColor: "#00a884",
  },
  pollFooter: {
    borderTop: "1px solid rgba(134, 150, 160, 0.25)",
    color: "#008069",
    fontSize: 13,
    fontWeight: 500,
    textAlign: "center",
    paddingTop: 8,
    marginTop: 2,
  },
  listBody: {
    fontSize: 14,
    lineHeight: "19px",
    color: "#111b21",
    marginBottom: 8,
    overflowWrap: "anywhere",
  },
  listFooter: {
    display: "block",
    color: "#667781",
    fontSize: 12,
    lineHeight: "16px",
    marginTop: 6,
    marginBottom: 4,
    overflowWrap: "anywhere",
  },
  listButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    borderTop: "1px solid rgba(134, 150, 160, 0.25)",
    color: "#008069",
    fontSize: 14,
    fontWeight: 500,
    textAlign: "center",
    paddingTop: 9,
    paddingBottom: 2,
    marginTop: 8,
    width: "100%",
    borderLeft: 0,
    borderRight: 0,
    borderBottom: 0,
    background: "transparent",
    cursor: "pointer",
    fontFamily: "inherit",
    "&:disabled": {
      cursor: "default",
      opacity: 0.7,
    },
  },
  listButtonIcon: {
    fontSize: 20,
  },
  audioTranscriptionBlock: {
    margin: "6px 6px 4px",
    padding: "8px 10px",
    borderLeft: "3px solid #00a884",
    borderRadius: 6,
    backgroundColor: "rgba(0, 168, 132, 0.08)",
    color: "#1f2c33",
    fontSize: 13,
    lineHeight: "18px",
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
  },
  audioTranscriptionTitle: {
    display: "block",
    marginBottom: 3,
    color: "#008069",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0,
    textTransform: "uppercase",
  },
  messageTranslationBlock: {
    margin: "6px 6px 4px",
    padding: "8px 10px",
    borderLeft: "3px solid #2f80ed",
    borderRadius: 6,
    backgroundColor: "rgba(47, 128, 237, 0.08)",
    color: "#1f2c33",
    fontSize: 13,
    lineHeight: "18px",
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
  },
  messageTranslationTitle: {
    display: "block",
    marginBottom: 3,
    color: "#1f6fd1",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0,
    textTransform: "uppercase",
  },
  listModalSection: {
    marginBottom: theme.spacing(2),
  },
  listModalSectionTitle: {
    color: theme.palette.text.secondary,
    fontSize: 13,
    fontWeight: 700,
    marginBottom: theme.spacing(0.75),
    textTransform: "uppercase",
  },
  listModalOption: {
    justifyContent: "flex-start",
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    textAlign: "left",
    textTransform: "none",
    cursor: "default",
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  listModalOptionText: {
    minWidth: 0,
    flex: "1 1 auto",
  },
  listModalOptionTitle: {
    display: "block",
    color: theme.palette.text.primary,
    fontSize: 14,
    fontWeight: 600,
    lineHeight: "18px",
    overflowWrap: "anywhere",
  },
  listModalOptionDescription: {
    display: "block",
    color: theme.palette.text.secondary,
    fontSize: 12,
    lineHeight: "16px",
    marginTop: 2,
    overflowWrap: "anywhere",
  },
}));

const reactionOptions = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

const reducer = (state, action) => {
  if (action.type === "REPLACE_MESSAGES") {
    return action.payload;
  }

  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages = [];

    messages.forEach((message) => {
      const messageIndex = state.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        state[messageIndex] = message;
      } else {
        newMessages.push(message);
      }
    });

    return [...newMessages, ...state];
  }

  if (action.type === "ADD_MESSAGE") {
    const newMessage = action.payload;
    const messageIndex = state.findIndex((m) => m.id === newMessage.id);

    if (messageIndex !== -1) {
      state[messageIndex] = newMessage;
    } else {
      state.push(newMessage);
    }

    return [...state].sort((a, b) => {
      const timeDiff =
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      if (timeDiff !== 0) return timeDiff;
      return String(a.id).localeCompare(String(b.id));
    });
  }

  if (action.type === "UPDATE_MESSAGE") {
    const messageToUpdate = action.payload;
    const messageIndex = state.findIndex((m) => m.id === messageToUpdate.id);

    if (messageIndex !== -1) {
      state[messageIndex] = messageToUpdate;
    }

    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const MessagesList = ({ ticketId, isGroup, showAiTools = false, readOnly = false }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [messagesList, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const lastMessageRef = useRef();
  const messagesContainerRef = useRef();

  const [selectedMessage, setSelectedMessage] = useState({});
  const [currentTicket, setCurrentTicket] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [transcribingMessageIds, setTranscribingMessageIds] = useState({});
  const [translatingMessageIds, setTranslatingMessageIds] = useState({});
  const [reactingMessageIds, setReactingMessageIds] = useState({});
  const [listOptionsMessage, setListOptionsMessage] = useState(null);
  const [reactionPickerAnchor, setReactionPickerAnchor] = useState(null);
  const [reactionPickerMessage, setReactionPickerMessage] = useState(null);
  const [reactionEmojiPickerOpen, setReactionEmojiPickerOpen] = useState(false);
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const currentTicketId = useRef(ticketId);
  const loadedTicketId = useRef(ticketId);
  const markingAsReadRef = useRef(false);
  const effectiveIsGroup = Boolean(isGroup || currentTicket?.isGroup || currentTicket?.contact?.isGroup);
  const [privacyCensorSettings, setPrivacyCensorSettings] = useState(() =>
    getStoredPrivacyCensorSettings(user?.id)
  );

  const getGroupSenderLabel = (message) => {
    const contact = message.contact || {};
    const alias = contact.name || "";
    const number = contact.number || "";

    if (alias && number && alias !== number) return `${alias} - ${number}`;
    return number || alias;
  };

  useEffect(() => {
    setPageNumber(1);
    setHasMore(false);

    currentTicketId.current = ticketId;
  }, [ticketId]);

  useEffect(() => {
    setPrivacyCensorSettings(getStoredPrivacyCensorSettings(user?.id));
  }, [user?.id]);

  useEffect(() => {
    const handlePrivacyCensorChanged = event => {
      setPrivacyCensorSettings(
        event.detail || getStoredPrivacyCensorSettings(user?.id)
      );
    };

    window.addEventListener(
      privacyCensorChangedEvent,
      handlePrivacyCensorChanged
    );

    return () => {
      window.removeEventListener(
        privacyCensorChangedEvent,
        handlePrivacyCensorChanged
      );
    };
  }, [user?.id]);

  const notifyTicketRead = ticket => {
    dispatchTicketRead({
      ticket: ticket
        ? {
            ...ticket,
            unreadMessages: 0,
          }
        : undefined,
      ticketId,
    });
  };

  const markCurrentTicketAsRead = async () => {
    if (markingAsReadRef.current || document.visibilityState !== "visible") return;

    markingAsReadRef.current = true;
    try {
      await api.put(`/messages/${ticketId}/read`);
      notifyTicketRead(currentTicket);
    } catch (err) {
      toastError(err);
    } finally {
      markingAsReadRef.current = false;
    }
  };

  useEffect(() => {
    if (String(loadedTicketId.current) !== String(ticketId) && pageNumber !== 1) {
      return;
    }

    setLoading(true);
    const fetchMessages = async () => {
      try {
        const { data } = await api.get("/messages/" + ticketId, {
          params: { pageNumber, ...(readOnly ? { readOnly: "true" } : {}) },
        });

        if (String(currentTicketId.current) === String(ticketId)) {
          dispatch({
            type: pageNumber === 1 ? "REPLACE_MESSAGES" : "LOAD_MESSAGES",
            payload: data.messages,
          });
          setCurrentTicket(data.ticket);
          setHasMore(data.hasMore);
          loadedTicketId.current = ticketId;
          setLoading(false);
          if (!readOnly) {
            notifyTicketRead(data.ticket);
          }
        }

        if (
          String(currentTicketId.current) === String(ticketId) &&
          pageNumber === 1 &&
          data.messages.length > 1
        ) {
          scrollToBottom();
        }
      } catch (err) {
        if (String(currentTicketId.current) === String(ticketId)) {
          setLoading(false);
        }
        toastError(err);
      }
    };
    fetchMessages();
  }, [pageNumber, ticketId]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("connect", () => socket.emit("joinChatBox", ticketId));

    socket.on("companyChatsCleared", () => {
      dispatch({ type: "REPLACE_MESSAGES", payload: [] });
      setCurrentTicket(prevTicket =>
        prevTicket
          ? { ...prevTicket, lastMessage: "", unreadMessages: 0 }
          : prevTicket
      );
      setHasMore(false);
    });

    socket.on("appMessage", (data) => {
      const eventTicketId = data.message?.ticketId || data.ticketId;
      if (String(eventTicketId) !== String(ticketId)) return;

      if (data.action === "create") {
        dispatch({ type: "ADD_MESSAGE", payload: data.message });
        scrollToBottom();

        if (!data.message.fromMe && !readOnly) {
          markCurrentTicketAsRead();
        }
      }

      if (data.action === "update") {
        dispatch({ type: "UPDATE_MESSAGE", payload: data.message });
      }

      if (data.action === "clear") {
        dispatch({ type: "REPLACE_MESSAGES", payload: [] });
        setCurrentTicket(data.ticket);
        setHasMore(false);
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [ticketId, readOnly]);

  const loadMore = () => {
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  };

  const scrollToBottom = () => {
    const messagesContainer =
      messagesContainerRef.current || document.getElementById("messagesList");

    if (!messagesContainer) return;

    messagesContainer.scrollTop = messagesContainer.scrollHeight;

    setTimeout(() => {
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }, 0);

    setTimeout(() => {
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
      lastMessageRef.current?.scrollIntoView({});
    }, 120);
  };

  useEffect(() => {
    if (pageNumber === 1 && messagesList.length > 0) {
      scrollToBottom();
    }
  }, [messagesList.length, pageNumber]);

  const handleScroll = (e) => {
    if (!hasMore) return;
    const { scrollTop } = e.currentTarget;

    if (scrollTop === 0) {
      e.currentTarget.scrollTop = 1;
    }

    if (loading) {
      return;
    }

    if (scrollTop < 50) {
      loadMore();
    }
  };

  const handleOpenMessageOptionsMenu = (e, message) => {
    setAnchorEl({
      top: e.clientY,
      left: e.clientX,
    });
    setSelectedMessage(message);
  };

  const handleCloseMessageOptionsMenu = (e) => {
    setAnchorEl(null);
  };

  const getMessageListMetadata = message => {
    const list = getMessageMetadata(message).list || {};
    const sections = Array.isArray(list.sections) ? list.sections : [];

    return {
      title: list.title || message?.body || "Lista",
      body: list.body || "",
      footer: list.footer || "",
      buttonText: list.buttonText || "Ver opciones",
      sections: sections
        .map(section => ({
          title: section.title || "Opciones",
          rows: Array.isArray(section.rows)
            ? section.rows
                .map(row => ({
                  title: row.title || "",
                  description: row.description || "",
                }))
                .filter(row => row.title)
            : [],
        }))
        .filter(section => section.rows.length),
    };
  };

  const handleOpenListOptions = message => {
    setListOptionsMessage(message);
  };

  const handleCloseListOptions = () => {
    setListOptionsMessage(null);
  };

  const handleOpenReactionPicker = (event, message) => {
    event.stopPropagation();
    setReactionEmojiPickerOpen(false);
    setReactionPickerAnchor(event.currentTarget);
    setReactionPickerMessage(message);
  };

  const handleCloseReactionPicker = () => {
    setReactionPickerAnchor(null);
    setReactionPickerMessage(null);
    setReactionEmojiPickerOpen(false);
  };

  const handleTranscribeAudio = async (message) => {
    handleCloseMessageOptionsMenu();
    setTranscribingMessageIds((current) => ({
      ...current,
      [message.id]: true,
    }));

    try {
      const { data } = await api.post(`/messages/${message.id}/transcribe`);
      if (data.message) {
        dispatch({ type: "UPDATE_MESSAGE", payload: data.message });
      }
    } catch (err) {
      toastError(err);
    } finally {
      setTranscribingMessageIds((current) => {
        const next = { ...current };
        delete next[message.id];
        return next;
      });
    }
  };

  const handleTranslateMessage = async (message, language) => {
    handleCloseMessageOptionsMenu();
    setTranslatingMessageIds((current) => ({
      ...current,
      [message.id]: true,
    }));

    try {
      const { data } = await api.post(`/messages/${message.id}/ai/translate`, {
        language,
      });
      if (data.message) {
        dispatch({ type: "UPDATE_MESSAGE", payload: data.message });
      }
    } catch (err) {
      toastError(err);
    } finally {
      setTranslatingMessageIds((current) => {
        const next = { ...current };
        delete next[message.id];
        return next;
      });
    }
  };

  const handleReactMessage = async (message, emoji) => {
    handleCloseMessageOptionsMenu();
    handleCloseReactionPicker();
    setReactingMessageIds((current) => ({
      ...current,
      [message.id]: true,
    }));

    try {
      const { data } = await api.post(`/messages/${message.id}/react`, {
        emoji,
      });
      if (data.message) {
        dispatch({ type: "UPDATE_MESSAGE", payload: data.message });
      }
    } catch (err) {
      toastError(err);
    } finally {
      setReactingMessageIds((current) => {
        const next = { ...current };
        delete next[message.id];
        return next;
      });
    }
  };

  const getCopyableMessageBody = (message) => {
    const body = String(getDisplayMessageBody(message) || message.body || "").trim();
    return body;
  };

  const copyTextToClipboard = async (text) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  };

  const handleCopyMessageBody = async (message) => {
    const body = getCopyableMessageBody(message);

    if (!body) {
      toast.info("Este mensaje no tiene texto para copiar.");
      return;
    }

    try {
      await copyTextToClipboard(body);
      toast.success("Mensaje copiado.");
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenFullReactionPicker = () => {
    setReactionEmojiPickerOpen(true);
  };

  const handleSelectCustomReaction = (emoji) => {
    const selectedEmoji = emoji?.native || emoji?.colons || "";

    if (!selectedEmoji || !reactionPickerMessage) return;

    handleReactMessage(reactionPickerMessage, selectedEmoji);
  };

  const getMediaFilename = (message) => {
    const metadata = getMessageMetadata(message);
    const originalName =
      metadata.mediaOriginalName ||
      metadata.originalName ||
      metadata.fileName ||
      metadata.filename;

    if (originalName) return String(originalName);
    if (!message.mediaUrl) return "";

    const filename = String(message.mediaUrl).split("/").pop() || "";

    try {
      return decodeURIComponent(filename);
    } catch (err) {
      return filename;
    }
  };

  const getDocumentExtension = (filename) => {
    const cleanFilename = String(filename || "").split("?")[0];
    const extension = cleanFilename.includes(".")
      ? cleanFilename.split(".").pop()
      : "";

    return extension ? extension.toUpperCase().slice(0, 5) : "FILE";
  };

  const isPdfDocument = (message, filename) => {
    const metadata = getMessageMetadata(message);

    return (
      /\.pdf(\?|$)/i.test(filename || "") ||
      /\.pdf(\?|$)/i.test(message.mediaUrl || "") ||
      /pdf/i.test(metadata.mimeType || metadata.mimetype || "")
    );
  };

  const getDocumentIconColor = (extension) => {
    const normalizedExtension = String(extension || "").toLowerCase();

    if (normalizedExtension === "pdf") return "#f15c5c";
    if (["doc", "docx"].includes(normalizedExtension)) return "#4285f4";
    if (["xls", "xlsx", "csv"].includes(normalizedExtension)) return "#21a366";
    if (["ppt", "pptx"].includes(normalizedExtension)) return "#d24726";
    if (["zip", "rar", "7z"].includes(normalizedExtension)) return "#7b61ff";

    return "#667781";
  };

  const formatDocumentSize = (size) => {
    if (size === null || size === undefined || size === "") return "";

    const numericSize = Number(size);

    if (!Number.isFinite(numericSize)) return String(size);
    if (numericSize < 1024) return `${numericSize} B`;
    if (numericSize < 1024 * 1024) return `${Math.round(numericSize / 1024)} kB`;

    return `${(numericSize / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getDocumentMeta = (message, filename) => {
    const metadata = getMessageMetadata(message);
    const metaParts = [];
    const pages = metadata.pages || metadata.pageCount;
    const size = formatDocumentSize(
      metadata.fileSize || metadata.size || metadata.fileLength
    );
    const extension = getDocumentExtension(filename);

    if (pages) {
      metaParts.push(`${pages} ${Number(pages) === 1 ? "pagina" : "paginas"}`);
    }

    metaParts.push(extension);

    if (size) {
      metaParts.push(String(size));
    }

    return metaParts;
  };

  const renderDocumentMedia = (message) => {
    const filename = getMediaFilename(message) || "Archivo adjunto";
    const extension = getDocumentExtension(filename);
    const metaParts = getDocumentMeta(message, filename);
    const showPreview = isPdfDocument(message, filename);

    return (
      <div className={classes.documentShell}>
        {showPreview && (
          <iframe
            title={filename}
            className={classes.documentPreview}
            src={message.mediaUrl}
          />
        )}
        <a
          className={classes.documentCard}
          href={message.mediaUrl}
          target="_blank"
          rel="noreferrer"
          title={filename}
        >
          <span
            className={classes.documentIconWrap}
            style={{ backgroundColor: getDocumentIconColor(extension) }}
          >
            <InsertDriveFile className={classes.documentIcon} />
            <span className={classes.documentExtension}>{extension}</span>
          </span>
          <span className={classes.documentInfo}>
            <span className={classes.documentName}>{filename}</span>
            <span className={classes.documentMeta}>
              {metaParts.map((part, index) => (
                <React.Fragment key={`${part}-${index}`}>
                  {index > 0 && <span>|</span>}
                  <span>{part}</span>
                </React.Fragment>
              ))}
            </span>
          </span>
          <GetApp className={classes.documentDownloadIcon} />
        </a>
      </div>
    );
  };

  const getDisplayMessageBody = (message) => {
    const body = String(message.body || "").trim();
    const mediaFilename = getMediaFilename(message);

    if (message.mediaUrl && mediaFilename && body === mediaFilename) {
      return "";
    }

    if (
      message.mediaUrl &&
      body &&
      !/\s/.test(body) &&
      /\.(jpe?g|png|gif|webp|bmp|svg|pdf|docx?|xlsx?|pptx?|txt|csv|zip|rar|7z|mp3|ogg|oga|wav|webm|mp4|mov|avi|mkv)$/i.test(body)
    ) {
      return "";
    }

    return message.body || "";
  };

  const renderMessageBody = (message) => {
    if (privacyCensorSettings.conversations) {
      return <span className={classes.censoredText}>Mensaje privado</span>;
    }

    if (["location", "poll", "list"].includes(message.mediaType)) {
      return null;
    }

    const body = getDisplayMessageBody(message);

    return body ? <MarkdownWrapper forceInline={false}>{body}</MarkdownWrapper> : null;
  };

  const parseLocationBody = (body = "") => {
    const parts = String(body || "").split("|");
    const firstPart = parts[0] || "";
    const secondPart = parts[1] || "";

    if (parts.length === 2) {
      return {
        image: firstPart,
        link: firstPart,
        description: secondPart,
      };
    }

    return {
      image: firstPart,
      link: secondPart,
      description: parts.slice(2).join("|") || null,
    };
  };

  const checkMessageMedia = (message) => {
    if (
      privacyCensorSettings.conversations &&
      ["location", "vcard", "poll", "list"].includes(message.mediaType)
    ) {
      return <span className={classes.censoredText}>Contenido privado</span>;
    }

    if (message.mediaType === "poll") {
      return renderPollPreview(message);
    }
    else if (message.mediaType === "list") {
      return renderListPreview(message);
    }
    else if (message.mediaType === "location" && message.body.split('|').length >= 2) {
      const location = parseLocationBody(message.body);

      return (
        <LocationPreview
          image={location.image}
          link={location.link}
          description={location.description}
        />
      )
    }
    else if (message.mediaType === "vcard") {
      //console.log("vcard")
      //console.log(message)
      let array = message.body.split("\n");
      let obj = [];
      let contact = "";
      for (let index = 0; index < array.length; index++) {
        const v = array[index];
        let values = v.split(":");
        for (let ind = 0; ind < values.length; ind++) {
          if (values[ind].indexOf("+") !== -1) {
            obj.push({ number: values[ind] });
          }
          if (values[ind].indexOf("FN") !== -1) {
            contact = values[ind + 1];
          }
        }
      }
      return (
        <VcardPreview
          contact={contact}
          numbers={obj[0]?.number}
          connectionId={currentTicket?.connectionId}
        />
      )
    }
    /*else if (message.mediaType === "multi_vcard") {
      console.log("multi_vcard")
      console.log(message)
    	
      if(message.body !== null && message.body !== "") {
        let newBody = JSON.parse(message.body)
        return (
          <>
            {
            newBody.map(v => (
              <VcardPreview
                contact={v.name}
                numbers={v.number}
                connectionId={currentTicket?.connectionId}
              />
            ))
            }
          </>
        )
      } else return (<></>)
    }*/
    else if (message.mediaType === "image") {
      return (
        <div
          className={clsx({
            [classes.censoredMedia]: privacyCensorSettings.conversations,
          })}
        >
          <ModalImageCors imageUrl={message.mediaUrl} />
        </div>
      );
    } else if (message.mediaType === "audio") {
      return privacyCensorSettings.conversations ? (
        <span className={classes.censoredText}>Audio privado</span>
      ) : (
        <Audio url={message.mediaUrl} />
      )
    } else if (message.mediaType === "video") {
      return (
        <video
          className={clsx(classes.messageMedia, {
            [classes.censoredMedia]: privacyCensorSettings.conversations,
          })}
          src={message.mediaUrl}
          controls
        />
      );
    } else {
      if (privacyCensorSettings.conversations) {
        return <span className={classes.censoredText}>Archivo privado</span>;
      }

      return (
        renderDocumentMedia(message)
      );
    }
  };

  const renderMessageAck = (message) => {
    if (message.ack === 0) {
      return <AccessTime fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 1) {
      return <Done fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 2) {
      return <DoneAll fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 3 || message.ack === 4) {
      return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />;
    }
  };

  const renderDailyTimestamps = (message, index) => {
    if (index === 0) {
      return (
        <span
          className={classes.dailyTimestamp}
          key={`timestamp-${message.id}`}
        >
          <div className={classes.dailyTimestampText}>
            {format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy")}
          </div>
        </span>
      );
    }
    if (index < messagesList.length - 1) {
      let messageDay = parseISO(messagesList[index].createdAt);
      let previousMessageDay = parseISO(messagesList[index - 1].createdAt);

      if (!isSameDay(messageDay, previousMessageDay)) {
        return (
          <span
            className={classes.dailyTimestamp}
            key={`timestamp-${message.id}`}
          >
            <div className={classes.dailyTimestampText}>
              {format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy")}
            </div>
          </span>
        );
      }
    }
    if (index === messagesList.length - 1) {
      return (
        <div
          key={`ref-${message.createdAt}`}
          ref={lastMessageRef}
          style={{ float: "left", clear: "both" }}
        />
      );
    }
  };

  const renderMessageDivider = (message, index) => {
    if (index < messagesList.length && index > 0) {
      let messageUser = messagesList[index].fromMe;
      let previousMessageUser = messagesList[index - 1].fromMe;

      if (messageUser !== previousMessageUser) {
        return (
          <span style={{ marginTop: 16 }} key={`divider-${message.id}`}></span>
        );
      }
    }
  };

  const renderQuotedMessage = (message) => {
    const metadata = getMessageMetadata(message);
    const externalQuotedMsg =
      metadata.externalQuotedMsg ||
      (metadata.externalQuotedMsgId
        ? {
            id: metadata.externalQuotedMsgId,
            body: "Historia no guardada",
            fromMe: true,
            source: "status",
          }
        : null);
    const quotedMsg = message.quotedMsg || externalQuotedMsg;
    const quotedFromMe = Boolean(quotedMsg?.fromMe);
    const quotedBody = quotedMsg?.body || "Mensaje citado";
    const quotedName =
      externalQuotedMsg?.source === "status"
        ? "Historia de WhatsApp"
        : quotedMsg?.contact?.name;

    if (!quotedMsg) return null;

    return (
      <div
        className={clsx(classes.quotedContainerLeft, {
          [classes.quotedContainerRight]: message.fromMe,
        })}
      >
        <span
          className={clsx(classes.quotedSideColorLeft, {
            [classes.quotedSideColorRight]: quotedFromMe,
          })}
        ></span>
        <div className={classes.quotedMsg}>
          {(!quotedFromMe || externalQuotedMsg?.source === "status") && (
            <span
              className={clsx(classes.messageContactName, {
                [classes.censoredText]: privacyCensorSettings.conversations,
              })}
            >
              {privacyCensorSettings.conversations
                ? "Contacto privado"
                : quotedName}
            </span>
          )}
          <span
            className={clsx({
              [classes.censoredText]: privacyCensorSettings.conversations,
            })}
          >
            {privacyCensorSettings.conversations
              ? "Mensaje citado privado"
              : quotedBody}
          </span>
        </div>
      </div>
    );
  };

  const getMessageMetadata = (message) => {
    if (!message.metadata) return {};
    if (typeof message.metadata === "object") return message.metadata;

    try {
      return JSON.parse(message.metadata);
    } catch (err) {
      return {};
    }
  };

  const getMessageReactions = (message) => {
    const reactions = getMessageMetadata(message).reactions;

    if (!Array.isArray(reactions)) return [];

    return reactions
      .map((reaction, index) => ({
        id: reaction?.id || `${message.id}-reaction-${index}`,
        emoji: String(reaction?.emoji || "").trim(),
      }))
      .filter((reaction) => reaction.emoji);
  };

  const renderMessageReactions = (message) => {
    const reactions = getMessageReactions(message);

    if (!reactions.length) return null;

    return (
      <div
        className={clsx(classes.messageReactions, {
          [classes.messageReactionsRight]: message.fromMe,
        })}
      >
        {reactions.map((reaction) => (
          <span className={classes.messageReactionChip} key={reaction.id}>
            {privacyCensorSettings.conversations ? "..." : reaction.emoji}
          </span>
        ))}
      </div>
    );
  };

  const renderMessageQuickActions = (message) => {
    if (message.isDeleted) return null;

    const canReact = !readOnly && !message.internalNote;
    const canCopy = Boolean(getCopyableMessageBody(message));

    if (!canReact && !canCopy) return null;

    return (
      <div
        className={clsx(classes.messageQuickActions, {
          [classes.messageQuickActionsRight]: message.fromMe,
          [classes.messageQuickActionsOpen]: reactionPickerMessage?.id === message.id,
        })}
      >
        {canReact && (
          <IconButton
            size="small"
            aria-label="Reaccionar"
            disableRipple
            disableFocusRipple
            disabled={Boolean(reactingMessageIds[message.id])}
            className={clsx(classes.quickActionButton, {
              [classes.reactionEntryButtonOpen]: reactionPickerMessage?.id === message.id,
            })}
            onClick={(event) => handleOpenReactionPicker(event, message)}
          >
            <InsertEmoticon fontSize="small" />
          </IconButton>
        )}
        {canCopy && (
          <IconButton
            size="small"
            aria-label="Copiar mensaje"
            disableRipple
            disableFocusRipple
            className={classes.quickActionButton}
            onClick={() => handleCopyMessageBody(message)}
          >
            <FileCopy fontSize="small" />
          </IconButton>
        )}
      </div>
    );
  };

  const renderReactionPicker = () => {
    if (!reactionPickerMessage) return null;

    const reacting = Boolean(reactingMessageIds[reactionPickerMessage.id]);

    return (
      <Popover
        open={Boolean(reactionPickerAnchor)}
        anchorEl={reactionPickerAnchor}
        onClose={handleCloseReactionPicker}
        classes={{
          paper: clsx(classes.reactionPickerPaper, {
            [classes.reactionPickerPaperFull]: reactionEmojiPickerOpen,
          }),
        }}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        {reactionEmojiPickerOpen ? (
          <ClickAwayListener onClickAway={handleCloseReactionPicker}>
            <div className={classes.reactionEmojiPickerContent}>
              <Picker
                perLine={8}
                showPreview={false}
                showSkinTones={false}
                onSelect={handleSelectCustomReaction}
              />
            </div>
          </ClickAwayListener>
        ) : (
          <div className={classes.reactionPickerContent}>
            {reactionOptions.map(emoji => (
              <IconButton
                key={emoji}
                size="small"
                disableRipple
                disableFocusRipple
                disabled={reacting}
                className={classes.reactionPickerButton}
                onClick={() => handleReactMessage(reactionPickerMessage, emoji)}
                title={emoji}
              >
                {emoji}
              </IconButton>
            ))}
            <IconButton
              size="small"
              disableRipple
              disableFocusRipple
              disabled={reacting}
              className={classes.reactionPickerButton}
              onClick={handleOpenFullReactionPicker}
              title="Mas reacciones"
            >
              <Add className={classes.reactionPickerPlus} />
            </IconButton>
          </div>
        )}
      </Popover>
    );
  };

  const renderAudioTranscription = (message) => {
    const transcription = getMessageMetadata(message).audioTranscription;
    const text = String(transcription?.text || "").trim();

    if (!text) return null;

    return (
      <div className={classes.audioTranscriptionBlock}>
        <span className={classes.audioTranscriptionTitle}>Transcripción</span>
        <MarkdownWrapper forceInline={false}>{text}</MarkdownWrapper>
      </div>
    );
  };

  const renderMediaTranscription = (message) => {
    const transcription = getMessageMetadata(message).mediaTranscription;
    const text = String(transcription?.text || "").trim();

    if (!text) return null;

    return (
      <div className={classes.audioTranscriptionBlock}>
        <span className={classes.audioTranscriptionTitle}>Texto transcrito</span>
        <MarkdownWrapper forceInline={false}>{text}</MarkdownWrapper>
      </div>
    );
  };

  const renderMessageTranslation = (message) => {
    const translation = getMessageMetadata(message).aiTranslation;
    const text = String(translation?.text || "").trim();

    if (!text) return null;

    return (
      <div className={classes.messageTranslationBlock}>
        <span className={classes.messageTranslationTitle}>Traducción</span>
        <MarkdownWrapper forceInline={false}>{text}</MarkdownWrapper>
      </div>
    );
  };

  const renderPollPreview = (message) => {
    const poll = getMessageMetadata(message).poll || {};
    const options = Array.isArray(poll.options) ? poll.options : [];
    const totalVotes = Number(poll.totalVotes || 0);
    const selectableOptionsCount = Number(poll.selectableOptionsCount || 1);
    const allowMultiple =
      poll.allowMultiple !== undefined
        ? Boolean(poll.allowMultiple)
        : poll.type === "pollCreationMessage" || selectableOptionsCount > 1;

    return (
      <div className={classes.pollBubble}>
        <div className={classes.pollTitle}>{poll.title || message.body || "Encuesta"}</div>
        <div className={classes.pollHint}>
          <DoneAll style={{ fontSize: 16 }} />
          <span>
            {allowMultiple
              ? "Selecciona una opción o más."
              : "Selecciona una opción."}
          </span>
        </div>
        {options.map((option) => {
          const votes = Number(option.votes || option.voters?.length || 0);
          const percentage =
            option.percentage !== undefined
              ? Number(option.percentage)
              : totalVotes
              ? Math.round((votes / totalVotes) * 100)
              : 0;
          const selected = votes > 0;

          return (
            <div className={classes.pollOption} key={option.id || option.name}>
              <span
                className={clsx(classes.pollCheck, {
                  [classes.pollCheckSelected]: selected,
                })}
              >
                {selected && <Done style={{ fontSize: 14 }} />}
              </span>
              <span className={classes.pollOptionName}>{option.name}</span>
              <span className={classes.pollVotes}>{votes}</span>
              <span className={classes.pollProgressTrack}>
                <span
                  className={classes.pollProgressBar}
                  style={{ width: `${Math.min(100, Math.max(0, percentage))}%` }}
                />
              </span>
            </div>
          );
        })}
        <div className={classes.pollFooter}>
          {totalVotes} {totalVotes === 1 ? "voto" : "votos"}
        </div>
      </div>
    );
  };

  const renderListPreview = (message) => {
    const list = getMessageListMetadata(message);

    return (
      <div className={classes.pollBubble}>
        <div className={classes.pollTitle}>{list.title}</div>
        {list.body && <div className={classes.listBody}>{list.body}</div>}
        {list.footer && <span className={classes.listFooter}>{list.footer}</span>}
        <button
          type="button"
          className={classes.listButton}
          onClick={() => handleOpenListOptions(message)}
        >
          <FormatListBulleted className={classes.listButtonIcon} />
          <span>{list.buttonText}</span>
        </button>
      </div>
    );
  };

  const renderListOptionsDialog = () => {
    if (!listOptionsMessage) return null;

    const list = getMessageListMetadata(listOptionsMessage);

    return (
      <Dialog
        open={Boolean(listOptionsMessage)}
        onClose={handleCloseListOptions}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{list.buttonText}</DialogTitle>
        <DialogContent dividers>
          {list.body && (
            <Typography variant="body2" paragraph>
              {list.body}
            </Typography>
          )}
          {list.sections.map((section, sectionIndex) => (
            <div className={classes.listModalSection} key={`${section.title}-${sectionIndex}`}>
              <Typography className={classes.listModalSectionTitle}>
                {section.title}
              </Typography>
              {section.rows.map((row, rowIndex) => (
                <React.Fragment key={`${section.title}-${row.title}-${rowIndex}`}>
                  <Button
                    fullWidth
                    className={classes.listModalOption}
                    disableRipple
                  >
                    <span className={classes.listModalOptionText}>
                      <span className={classes.listModalOptionTitle}>
                        {row.title}
                      </span>
                      {row.description && (
                        <span className={classes.listModalOptionDescription}>
                          {row.description}
                        </span>
                      )}
                    </span>
                  </Button>
                  {rowIndex < section.rows.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </div>
          ))}
          {list.footer && (
            <Typography variant="caption" color="textSecondary">
              {list.footer}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseListOptions} color="primary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const getMessageSourceLabel = (message) => {
    const metadata = getMessageMetadata(message);

    if (metadata.sourceLabel) return metadata.sourceLabel;
    if (message.source === "ai" || message.source === "ai_tool") return "IA";
    if (message.source === "agent") {
      return (
        metadata.userName ||
        metadata.connectionNumber ||
        message.ticket?.connection?.number ||
        currentTicket?.connection?.number ||
        message.ticket?.connection?.name ||
        currentTicket?.connection?.name ||
        "Conexión"
      );
    }
    if (message.source === "chatbot") return "Chatbot";
    if (message.source === "reminder") return "Recordatorio programado";
    if (message.source === "funnel") return "Embudo";
    return null;
  };

  const normalizeSourceLabel = (value) =>
    String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

  const getMessageSourceLabelClass = (message) => {
    const label = normalizeSourceLabel(getMessageSourceLabel(message));

    return clsx(classes.messageSourceLabel, {
      [classes.aiSourceLabel]: message.source === "ai" || message.source === "ai_tool",
      [classes.campaignSourceLabel]:
        message.source === "campaign" || label.includes("campana"),
      [classes.agendaSourceLabel]:
        message.source === "agenda" || label.includes("agenda"),
      [classes.webhookSourceLabel]:
        message.source === "webhook" || label.includes("webhook"),
      [classes.quickReplySourceLabel]:
        label.includes("respuesta rapida") || label.includes("quick reply"),
      [classes.agentSourceLabel]:
        (message.source === "agent" ||
          message.source === "chatbot" ||
          message.source === "reminder") &&
        !label.includes("respuesta rapida") &&
        !label.includes("quick reply"),
      [classes.funnelSourceLabel]: message.source === "funnel",
    });
  };

  const renderMessages = () => {
    if (messagesList.length > 0) {
      const viewMessagesList = messagesList.map((message, index) => {
        if (!message.fromMe) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}
              <div className={classes.messageLeft}>
                {renderMessageQuickActions(message)}
                {!readOnly && (
                  <IconButton
                    variant="contained"
                    size="small"
                    id="messageActionsButton"
                    disabled={message.isDeleted}
                    className={classes.messageActionsButton}
                    onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                  >
                    <ExpandMore />
                  </IconButton>
                )}
                {effectiveIsGroup && getGroupSenderLabel(message) && (
                  <span
                    className={clsx(classes.messageContactName, {
                      [classes.censoredText]:
                        privacyCensorSettings.conversations,
                    })}
                  >
                    {privacyCensorSettings.conversations
                      ? "Contacto privado"
                      : getGroupSenderLabel(message)}
                  </span>
                )}
                {(message.mediaUrl || message.mediaType === "location" || message.mediaType === "vcard" || message.mediaType === "poll" || message.mediaType === "list"
                  //|| message.mediaType === "multi_vcard" 
                ) && checkMessageMedia(message)}
                <div className={classes.textContentItem}>
                  {(message.quotedMsg ||
                    getMessageMetadata(message).externalQuotedMsg ||
                    getMessageMetadata(message).externalQuotedMsgId) &&
                    renderQuotedMessage(message)}
                  {renderMessageBody(message)}
                  {renderMediaTranscription(message)}
                  {renderMessageTranslation(message)}
                  {message.mediaType === "audio" && renderAudioTranscription(message)}
                  <span className={classes.timestamp}>
                    {format(parseISO(message.createdAt), "HH:mm")}
                  </span>
                </div>
                {renderMessageReactions(message)}
              </div>
            </React.Fragment>
          );
        } else {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}
              <div
                className={
                  message.internalNote
                    ? classes.messageInternalNote
                    : classes.messageRight
                }
              >
                {renderMessageQuickActions(message)}
                {!readOnly && (
                  <IconButton
                    variant="contained"
                    size="small"
                    id="messageActionsButton"
                    disabled={message.isDeleted}
                    className={classes.messageActionsButton}
                    onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                  >
                    <ExpandMore />
                  </IconButton>
                )}
                {(message.mediaUrl || message.mediaType === "location" || message.mediaType === "vcard" || message.mediaType === "poll" || message.mediaType === "list"
                  //|| message.mediaType === "multi_vcard" 
                ) && checkMessageMedia(message)}
                <div
                  className={clsx(classes.textContentItem, {
                    [classes.textContentItemDeleted]: message.isDeleted,
                  })}
                >
                  {message.internalNote ? (
                    message.source === "ai_tool" || message.source === "funnel" ? (
                      <span className={getMessageSourceLabelClass(message)}>
                        {getMessageSourceLabel(message)}
                      </span>
                    ) : (
                      <span className={classes.internalNoteLabel}>
                        Nota interna
                      </span>
                    )
                  ) : (
                    getMessageSourceLabel(message) && (
                      <span className={getMessageSourceLabelClass(message)}>
                        {getMessageSourceLabel(message)}
                      </span>
                    )
                  )}
                  {message.isDeleted && (
                    <Block
                      color="disabled"
                      fontSize="small"
                      className={classes.deletedIcon}
                    />
                  )}
                  {(message.quotedMsg ||
                    getMessageMetadata(message).externalQuotedMsg ||
                    getMessageMetadata(message).externalQuotedMsgId) &&
                    renderQuotedMessage(message)}
                  {renderMessageBody(message)}
                  {renderMediaTranscription(message)}
                  {renderMessageTranslation(message)}
                  {message.mediaType === "audio" && renderAudioTranscription(message)}
                  <span className={classes.timestamp}>
                    {format(parseISO(message.createdAt), "HH:mm")}
                    {!message.internalNote && renderMessageAck(message)}
                  </span>
                </div>
                {renderMessageReactions(message)}
              </div>
            </React.Fragment>
          );
        }
      });
      return viewMessagesList;
    } else {
      return <div>Say hello to your new contact!</div>;
    }
  };

  return (
    <div className={classes.messagesListWrapper}>
      {!readOnly && (
        <MessageOptionsMenu
          message={selectedMessage}
          anchorEl={anchorEl}
          menuOpen={messageOptionsMenuOpen}
          handleClose={handleCloseMessageOptionsMenu}
          handleTranscribeAudio={handleTranscribeAudio}
          transcribingAudio={Boolean(transcribingMessageIds[selectedMessage.id])}
          handleTranslateMessage={handleTranslateMessage}
          translatingMessage={Boolean(translatingMessageIds[selectedMessage.id])}
        />
      )}
      {renderListOptionsDialog()}
      {renderReactionPicker()}
      <div
        id="messagesList"
        ref={messagesContainerRef}
        className={clsx(classes.messagesList, {
          [classes.messagesListWithAiTools]: showAiTools,
        })}
        onScroll={handleScroll}
      >
        {messagesList.length > 0 ? renderMessages() : []}
      </div>
      {loading && messagesList.length === 0 && (
        <div>
          <CircularProgress className={classes.circleLoading} />
        </div>
      )}
    </div>
  );
};

export default MessagesList;

